import React, { useMemo } from 'react';

import ArrowBack from '@mui/icons-material/ArrowBack';
import MUIBreadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import QAmountField from 'components/QAmountField';
import QIconButton from 'components/QIconButton';

import type { Breadcrumb } from '../types';

const useStyles = makeStyles()((theme) => ({
  breadcrumbWrapper: {
    marginBottom: 10,
  },
  crumbsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  crumb: {
    color: theme.palette.text.primary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&:focus': {
      color: theme.palette.text.primary,
    },
    '&:active': {
      color: theme.palette.text.primary,
    },
    '&:visited': {
      color: theme.palette.text.primary,
    },
  },
  crumbColor: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&:focus': {
      color: theme.palette.text.secondary,
    },
    '&:active': {
      color: theme.palette.text.secondary,
    },
    '&:visited': {
      color: theme.palette.text.secondary,
    },
  },
  lastCrumbColor: {
    color: theme.palette.text.primary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
    '&:focus': {
      color: theme.palette.text.primary,
    },
    '&:active': {
      color: theme.palette.text.primary,
    },
    '&:visited': {
      color: theme.palette.text.primary,
    },
  },
  finalCrumbInfo: {
    borderBottom: `1px solid ${theme.palette.border.light}`,
    paddingBottom: 5,
    marginBottom: 16,
  },
}));

interface BreadcrumbNavProps {
  breadcrumbs: Breadcrumb[];
  onBreadcrumbClick: (newBreadcrumbs: Breadcrumb[]) => void;
  hideBackArrow?: boolean;
}

export default function BreadcrumbNav({ breadcrumbs, onBreadcrumbClick, hideBackArrow = false }: BreadcrumbNavProps) {
  const { classes } = useStyles();

  const handleCrumbClick = (crumbIndex: number) => (e: React.MouseEvent) => {
    e?.stopPropagation();
    if (crumbIndex === breadcrumbs.length - 1) {
      return; // not going anywhere
    }
    onBreadcrumbClick(breadcrumbs.slice(0, crumbIndex + 1));
  };

  const backIconStyle = useMemo(
    () =>
      breadcrumbs.length <= 1 || hideBackArrow ? { width: 0, padding: 0, opacity: 0 } : { padding: 2, marginRight: 4 },
    [breadcrumbs.length, hideBackArrow],
  );

  const finalCrumb = breadcrumbs[breadcrumbs.length - 1];

  return (
    <div className={classes.breadcrumbWrapper}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <>
          <div className={classes.crumbsContainer}>
            <QIconButton
              onClick={handleCrumbClick(breadcrumbs.length - 2)}
              disabled={breadcrumbs.length === 1 || hideBackArrow}
              style={backIconStyle}
            >
              <ArrowBack />
            </QIconButton>
            <MUIBreadcrumbs maxItems={5}>
              {breadcrumbs.map((crumb, crumbIndex) => (
                <Link
                  underline='hover'
                  className={classes.crumb}
                  onClick={handleCrumbClick(crumbIndex)}
                  key={`crumb-${crumb.name}-${crumbIndex}`}
                  style={{ cursor: 'pointer' }}
                >
                  <Typography
                    className={crumbIndex === breadcrumbs.length - 1 ? classes.lastCrumbColor : classes.crumbColor}
                    component='span'
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {crumb.name}
                  </Typography>
                </Link>
              ))}
            </MUIBreadcrumbs>
          </div>
          {breadcrumbs.length > 1 && finalCrumb && (
            <div className={classes.finalCrumbInfo}>
              <Typography component='div' sx={{ fontSize: '0.875rem' }}>
                {finalCrumb.name}
              </Typography>
              <QAmountField
                expectedSign='positive'
                showSign={finalCrumb.showSign}
                variant='paragraphMedium'
                value={finalCrumb.total || 0}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
